import { useQuery } from '@apollo/client/react'
import {
  Badge,
  ErrorState,
  LoadingState,
  PageLayout,
  Section,
  buttonClasses,
  useLanguage,
  useTranslate,
  cn,
  PANEL_SURFACE,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { StudentCardLink } from '../app/links.js'
import { usePageTitle } from '../app/page-title.js'
import { AddStudentForm } from '../features/students/AddStudentForm.js'
import { PendingRequests } from '../features/students/PendingRequests.js'
import {
  MyStudentsDocument,
  TutorStudentConnectionRequestsDocument,
} from '../graphql/generated/operations.js'

/** Connected students and consent-based pending requests are separate lists. */
export function StudentsScreen() {
  const t = useTranslate()
  const language = useLanguage()
  usePageTitle(t('student.listTitle'))

  const { data, loading, error } = useQuery(MyStudentsDocument, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15000,
  })
  const requests = useQuery(TutorStudentConnectionRequestsDocument, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15000,
  })

  return (
    <PageLayout width="operational">
      <div className="grid items-start gap-6 desktop:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] desktop:gap-8">
        <Section id="add-student" title={t('student.addHeading')} className={PANEL_SURFACE}>
          <AddStudentForm
            labelledBy="add-student"
            pendingEmails={
              requests.data?.tutorStudentConnectionRequests.map(
                (request) => request.invitedEmail,
              ) ?? []
            }
          />
        </Section>

        <Section
          id="pending-requests"
          title={t('connections.pendingTitle')}
          className={cn(PANEL_SURFACE, 'h-full')}
        >
          {requests.loading && !requests.data ? (
            <LoadingState>{t('common.loading')}</LoadingState>
          ) : null}
          {requests.error ? <ErrorState>{t('connections.failed')}</ErrorState> : null}
          {requests.data ? (
            <PendingRequests requests={requests.data.tutorStudentConnectionRequests} />
          ) : null}
        </Section>
      </div>

      <Section id="students-list" title={t('student.yourStudents')}>
        {loading ? <LoadingState>{t('common.loading')}</LoadingState> : null}
        {error ? <ErrorState>{t('student.listError')}</ErrorState> : null}

        {data?.myStudents.length === 0 ? (
          <p className="text-body text-text-secondary">{t('student.noneYet')}</p>
        ) : null}

        {data?.myStudents.length ? (
          <ul className="divide-y divide-border">
            {data.myStudents.map((student) => (
              <li key={student.id}>
                <StudentCardLink
                  to="/$language/students/$studentId"
                  params={{ language, studentId: student.id }}
                  name={student.displayName}
                  // The age the server worked out, or nothing. It replaces the
                  // free-text school year that used to sit here, which was a
                  // different answer in every country and read by nothing.
                  meta={student.age === null ? null : t('student.age', [student.age])}
                  detail={
                    student.suspended
                      ? t('student.setAsideDetail')
                      : student.claimed
                        ? t('student.signedInAndReady')
                        : t('student.noAccountYet')
                  }
                  // A connection the plan is no longer holding still opens —
                  // its history is what the tutor is choosing between — but
                  // takes no new work, so the row says so and points to where
                  // it can be brought back.
                  trailing={
                    student.suspended ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge status="setAside" className="px-3 py-1.5">
                          {t('subscription.suspendedState')}
                        </Badge>
                        <Link
                          to="/$language/plan"
                          params={{ language }}
                          hash="students-set-aside"
                          className={buttonClasses({ variant: 'ghost', size: 'sm' })}
                        >
                          {t('subscription.bringBackOnPlan')}
                        </Link>
                      </div>
                    ) : undefined
                  }
                />
              </li>
            ))}
          </ul>
        ) : null}
      </Section>

      <p className="text-body-sm text-text-secondary">
        {t('student.lookingForScores')}{' '}
        <Link
          to="/$language/practices"
          params={{ language }}
          className={buttonClasses({ variant: 'ghost', size: 'sm' })}
        >
          {t('student.openAPractice')}
        </Link>
      </p>
    </PageLayout>
  )
}
